import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  BookOpen,
  ExternalLink,
  AlertTriangle,
  RotateCcw,
  ShieldAlert,
  ArrowLeft,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

import { useReferenceIndex, useReferenceManufacturer } from '@/features/service/hooks'
import type { ReferenceServiceItem } from '@/types/service'
import { requireFlow } from '@/features/auth/guards'

export const Route = createFileRoute('/_authenticated/service/reference')({
  beforeLoad: requireFlow('service'),
  component: ServiceReferencePage,
})

const SOURCE_LABEL: Record<string, string> = {
  official: 'Factory',
  dealer: 'Dealer/industry',
  community: 'Community',
}

function intervalLabel(item: ReferenceServiceItem): string {
  const parts: string[] = []
  if (item.interval_hours != null) parts.push(`${item.interval_hours} hrs`)
  if (item.interval_months != null) parts.push(`${item.interval_months} mo`)
  return parts.join(' or ')
}

function ServiceReferencePage() {
  const { data: reference, isLoading } = useReferenceIndex()
  const [selected, setSelected] = useState<string>('')

  const activeKey = selected || reference?.manufacturers[0]?.key || ''
  const { data: mfr } = useReferenceManufacturer(activeKey)

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link
          to="/service"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Service
        </Link>
        <h1 className="mt-2 text-3xl font-bold tracking-tight flex items-center gap-2">
          <BookOpen className="h-8 w-8" /> Service Reference
        </h1>
        <p className="text-muted-foreground">
          Manufacturer service intervals, known failure patterns, and curated recalls — sourced
          from factory manuals and dealer schedules.
        </p>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-4">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full md:col-span-3" />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-4">
          {/* Manufacturer list */}
          <div className="flex flex-col gap-1 rounded-lg border p-2">
            {reference?.manufacturers.map((m) => (
              <button
                key={m.key}
                onClick={() => setSelected(m.key)}
                className={cn(
                  'rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-accent',
                  m.key === activeKey && 'bg-accent font-medium',
                )}
              >
                <span className="block leading-tight">{m.name}</span>
                <span className="block text-xs text-muted-foreground">
                  {m.engine_type} · {m.item_count} items
                </span>
              </button>
            ))}
          </div>

          {/* Manufacturer detail */}
          <div className="flex flex-col gap-4 md:col-span-3">
            {!mfr ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>{mfr.name} — service intervals</CardTitle>
                    <CardDescription>
                      The marine-industry pattern is "X hours OR Y months, whichever comes first".
                      Apply a plan to a boat from its detail page.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Service item</TableHead>
                            <TableHead>Interval</TableHead>
                            <TableHead>Priority</TableHead>
                            <TableHead>Source</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mfr.items.map((item) => (
                            <TableRow key={item.key}>
                              <TableCell>
                                <div className="font-medium">{item.title}</div>
                                <div className="text-xs text-muted-foreground">
                                  {item.description}
                                </div>
                              </TableCell>
                              <TableCell className="whitespace-nowrap">
                                <span className="inline-flex items-center gap-1 text-sm">
                                  <RotateCcw className="h-3.5 w-3.5 text-muted-foreground" />
                                  {intervalLabel(item)}
                                </span>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="capitalize">
                                  {item.priority}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <a
                                  href={item.source_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-xs underline text-muted-foreground"
                                >
                                  {SOURCE_LABEL[item.source_type] ?? item.source_type}
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>

                {mfr.known_issues.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" /> Commonly reported failures
                      </CardTitle>
                      <CardDescription>
                        Corroborated failure patterns worth checking at each service.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3">
                      {mfr.known_issues.map((issue) => (
                        <div key={issue.summary} className="rounded-lg border p-3">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium">{issue.summary}</p>
                            <Badge variant="outline" className="capitalize">
                              {issue.severity}
                            </Badge>
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground">{issue.details}</p>
                          <a
                            href={issue.source_url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-flex items-center gap-1 text-xs underline text-muted-foreground"
                          >
                            Source <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShieldAlert className="h-4 w-4" /> Recalls & campaigns
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-3">
                    {mfr.recalls.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No curated entries for this brand. Check the{' '}
                        <a
                          href={
                            reference?.uscg_recalls_url ??
                            'https://uscgboating.org/content/recalls.php'
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          US Coast Guard recall database
                        </a>{' '}
                        — searchable by manufacturer and model.
                      </p>
                    ) : (
                      mfr.recalls.map((r) => (
                        <div key={r.title} className="rounded-lg border p-3">
                          <p className="font-medium">{r.title}</p>
                          <p className="text-sm text-muted-foreground">
                            Affected: {r.affected} · {r.opened}
                          </p>
                          <a
                            href={r.source_url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-flex items-center gap-1 text-xs underline text-muted-foreground"
                          >
                            Source <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
